import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { WBSButton } from '@/common/WBSButton';
import { FollowUpRequestBoardDetailModal } from '@/follow-up/FollowUpRequestBoardDetailModal';
import CoordinatorPendingRequestsTable from '@/tables/CoordinatorPendingRequestsTable';
import PartnerPendingFollowUpsTable from '@/tables/PartnerPendingFollowUpsTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/follow-ups/pending-requests/')({
  component: PendingRequestsScreen,
});

/**
 * The *Pending Requests* subnav of both follow-up portals — and the two sides
 * are a DIFFERENT TABLE over a DIFFERENT TABLE, not one list with two scopes:
 *
 * * the coordinator's (`.../follow-up-coordinator-group/follow-ups/
 *   pending-requests/pending-requests.php`) reads `follow_up_requests` for
 *   `coordinator_id = me AND status = 'adopted'` — requests they claimed from
 *   the board and have not yet converted;
 * * the partner's (`.../follow-up-partner-group/follow-ups/pending-requests/
 *   pending-requests.php`) reads `follow_ups` for `requested_partner_id = me
 *   AND partner_status = 'pending'` — invitations they have not answered — and
 *   ignores `follow_ups.status` entirely.
 *
 * The two even carry different HEADINGS, and the coordinator's is not the
 * subnav label: *Student Waiting For Follow-up* (`pending-requests.php:3`)
 * against the partner's *Pending Requests* (`pending-requests.php:4`).
 */
function PendingRequestsScreen() {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();
  const [detailRequestId, setDetailRequestId] = useState<number | null>(null);

  const isCoordinator = me?.profile_type === 'FOLLOWUP_COORDINATOR';
  const isPartner = me?.profile_type === 'FOLLOWUP_PARTNER';

  // ADMIN is deliberately absent, unlike the board's gate: both list endpoints
  // scope on the caller's own subtype id and `admins ∩ followup_coordinators`
  // is 0 rows, so admitting an admin here renders a 403 rather than a tab
  // (DIV-2). `isPending` is not optional — `/users/me` is pending on first
  // paint and a bare `!canView` bounces everyone, the coordinator included.
  //
  // Defence in depth over the same set the endpoints enforce, never the only
  // gate.
  if (isPending) return null;
  if (!isCoordinator && !isPartner) return <NotFoundScreen />;

  return (
    <PageContainer>
      <GroupHeaderWrapper>
        {/* A bare `Title`, not `TitleWithQuestion`: the tooltip legacy renders
            beside this heading is `wbs_contextual_helper(
            'follow_up_for_coordinators' | 'follow_up_for_partners')`, a
            WordPress help-tooltip ENTITY whose content is post data with no v3
            analogue and no migrator, so there is nothing for a `?` to open.
            `HelpModal` matches lowercased ENGLISH ids and falls back silently
            to the first item, so wiring one to a translated title would open
            the Courses help for every non-English user. */}
        <Title>
          {isCoordinator
            ? t('lessons.student-waiting-for-follow-up')
            : t('wbs.pending-requests')}
        </Title>
        {isCoordinator && (
          // `pending-requests.php:4-6`. The only header control either portal
          // renders, and its target is W4.5's board.
          <WBSButton as={Link} to="/follow-up-request-board" wide>
            {t('wbs.go-to-request-board')}
          </WBSButton>
        )}
      </GroupHeaderWrapper>
      {isCoordinator ? (
        <>
          <CoordinatorPendingRequestsTable onViewDetail={setDetailRequestId} />
          {/* The same popup the board renders — legacy's coordinator queue
              mounts `Actions\FollowUpReqeusts\ViewDetail`, whose
              `wbs_get_view_request_detail` handler is the one the board's
              action calls too. The endpoint's second scope arm
              (`request.coordinator_id == caller.id`) is what serves it here:
              these rows are `adopted` and so are off the board. */}
          <FollowUpRequestBoardDetailModal
            requestId={detailRequestId}
            onClose={() => setDetailRequestId(null)}
          />
        </>
      ) : (
        <PartnerPendingFollowUpsTable />
      )}
    </PageContainer>
  );
}
